import React from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Clock, CalendarDays, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { usePlayer, PREVIEW_LABEL } from "@/context/PlayerContext";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import { PlatformRow } from "@/components/site/PlatformRow";
import { mediaUrl } from "@/lib/api";
import { toPlayable, fmtShortDate } from "@/lib/release";
import { TrackRow } from "@/components/site/TrackRow";
import { WatchVideoButton } from "@/components/site/VideoModal";
import { LyricsTab } from "@/components/site/LyricsTab";

const TrackCard = ({ release, featured = false }) => {
  const player = usePlayer();
  const playable = toPlayable(release);
  const hasPreview = Boolean(playable);
  const full = Boolean(release.full_song);
  const isActive = player.isActive(release.id);
  const playing = isActive && player.isPlaying;

  const onToggle = () => {
    if (isActive) player.toggle();
    else if (playable) player.playTrack(playable, 0);
  };

  return (
    <div className="frame p-5 sm:p-7 h-full flex flex-col" style={{ backgroundColor: "var(--ink-raised)" }} data-testid={`release-card-${release.id}`}>
      <div className={`flex ${featured ? "flex-col lg:flex-row" : "flex-col"} gap-6`}>
        <div className={featured ? "lg:w-[46%] shrink-0" : ""}>
          <div className="relative" style={{ backgroundColor: "#000", padding: 8, borderRadius: "var(--radius)", border: "1px solid rgba(200,155,60,.3)" }}>
            <img src={mediaUrl(release.artwork_url) || "/icon-512.png"} alt={`${release.title} cover art`} className="w-full h-auto" style={{ borderRadius: "var(--radius)" }} />
            {playing && (
              <span className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 font-caps"
                style={{ fontSize: ".5rem", background: "var(--ink)", color: "var(--rose)", borderRadius: "var(--radius)", border: "1px solid var(--rose)" }} data-testid="playing-indicator">
                <span className="h-1.5 w-1.5 rounded-full candle-glow" style={{ background: "var(--rose)" }} /> Playing
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 flex flex-col">
          <div className="flex items-center gap-3">
            <span className="badge-type">{release.type}</span>
            {featured && <span className="font-caps text-rose" style={{ fontSize: ".55rem" }}>Featured</span>}
          </div>

          <h3 className="font-display foil-text mt-3" style={{ fontSize: featured ? "clamp(2rem,4vw,3rem)" : "1.9rem", lineHeight: 1.05 }}>{release.title}</h3>
          {release.subtitle && <p className="font-script text-gold" style={{ fontSize: "1.9rem", lineHeight: 1 }}>{release.subtitle}</p>}
          <p className="mt-2" style={{ color: "var(--ash)", fontSize: ".85rem" }}>{release.credit || "P. Frazy · Heart N Soul Records"}</p>

          <div className="flex flex-wrap gap-5 mt-3" style={{ color: "var(--ash)", fontSize: ".8rem" }}>
            {release.runtime && <span className="inline-flex items-center gap-2"><Clock size={13} /> {release.runtime}</span>}
            {release.release_at && <span className="inline-flex items-center gap-2"><CalendarDays size={13} /> {fmtShortDate(release.release_at)}</span>}
          </div>

          {featured && release.blurb && <p className="mt-4 max-w-lg" style={{ color: "var(--parchment)" }}>{release.blurb}</p>}
          {release.epigraph && <p className="font-script text-gold mt-4" style={{ fontSize: "2rem", lineHeight: 1 }}>{release.epigraph}</p>}

          <div className="mt-5 flex flex-wrap gap-3 items-start">
            {hasPreview ? (
              <div>
                <button className="btn-gold" onClick={onToggle} data-testid={`preview-btn-${release.id}`}>
                  {playing ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" />}
                  {full ? (playing ? "Pause song" : "Play song") : (playing ? "Pause preview" : "Play preview")}
                </button>
                <p className="mt-2" style={{ color: "var(--ash)", fontSize: ".72rem" }} data-testid={`play-note-${release.id}`}>
                  {full ? "Full song" : `${PREVIEW_LABEL} preview — hear the full song on your platform.`}
                </p>
                <WatchVideoButton release={release} style={{ marginTop: ".75rem" }} />
              </div>
            ) : (
              <div>
                <p className="font-caps text-gold" style={{ fontSize: ".68rem" }} data-testid={`no-preview-${release.id}`}>Full song on your platform</p>
                <WatchVideoButton release={release} style={{ marginTop: ".75rem" }} />
              </div>
            )}
          </div>

          {(release.type === "ALBUM" || release.type === "EP") && release.tracklist?.length > 0 && (
            <Accordion type="single" collapsible className="mt-6">
              <AccordionItem value="tracklist" style={{ borderColor: "rgba(200,155,60,.2)" }}>
                <AccordionTrigger className="font-caps text-gold hover:no-underline" style={{ fontSize: ".68rem" }} data-testid={`tracklist-toggle-${release.id}`}>
                  Tracklist ({release.tracklist.length})
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid gap-3 pt-3">
                    {release.tracklist.map((t, i) => (
                      <TrackRow key={i} release={release} track={t} index={i} compact />
                    ))}
                  </div>
                  <Link
                    to={`/album/${release.id}`}
                    className="font-caps link-underline inline-flex items-center gap-2 mt-4"
                    style={{ fontSize: ".62rem" }}
                    data-testid={`album-link-${release.id}`}
                  >
                    View album page <ArrowRight size={12} />
                  </Link>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          <LyricsTab release={release} />

          <div className="mt-5 pt-5" style={{ borderTop: "var(--rule)" }}>
            <p className="font-caps text-gold mb-3" style={{ fontSize: ".82rem", fontWeight: 700, letterSpacing: ".18em" }}>Listen on</p>
            <PlatformRow links={release.links} fallback={release.smart_link || release.presave} />
          </div>
        </div>
      </div>
    </div>
  );
};

export const MusicGrid = ({ music = [] }) => {
  if (music.length === 0) return null;
  const featured = music.find((r) => r.featured) || music[0];
  const rest = music.filter((r) => r.id !== featured.id);
  return (
    <section id="music" className="section-pad" data-testid="music-section">
      <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
        <Reveal>
          <p className="font-caps text-gold" style={{ fontSize: ".72rem" }}>The Music</p>
          <h2 className="font-display foil-text mt-3" style={{ fontSize: "var(--t-h2)", lineHeight: 1.05 }}>Out now</h2>
        </Reveal>
        {featured && <Reveal className="mt-12" delay={0.05}><TrackCard release={featured} featured /></Reveal>}
        <RevealGroup className="grid md:grid-cols-2 gap-8 mt-8">
          {rest.map((r) => <RevealItem key={r.id}><TrackCard release={r} /></RevealItem>)}
        </RevealGroup>
      </div>
    </section>
  );
};
